import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navigation from '../components/Navigation';
import HeroCard from '../components/HeroCard';
import PrimaryButton from '../components/PrimaryButton';
import SnowflakesEffect from '../components/SnowflakesEffect';
import { isChristmasPeriod } from '../utils/christmasTheme';
import { trackThanksBeerGenerated, trackLinkCopied, trackButtonClick } from '../utils/analytics';
import giveThanksImage from '../images/givethanks.png';
import '../styles/GiveThanksPage.css';
import '../styles/christmas.css';

// Utility function to encode kudos message to base64
const encodeMessage = (message: string): string => {
  try {
    return btoa(encodeURIComponent(message));
  } catch (e) {
    return message;
  }
};

// Default name constants
const DEFAULT_SENDER_NAME = 'Someone';
const DEFAULT_RECIPIENT_NAME = 'Friend';

const GiveThanksPage: React.FC = () => {
  const navigate = useNavigate();
  const { company } = useParams<{ company?: string }>();
  const [senderName, setSenderName] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [kudosMessage, setKudosMessage] = useState('');
  const [shareableUrl, setShareableUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const isChristmas = isChristmasPeriod();

  const handleGiveThanks = () => {
    const fromParam = senderName.trim() || DEFAULT_SENDER_NAME;
    const toParam = recipientName.trim() || DEFAULT_RECIPIENT_NAME;
    const params = new URLSearchParams({
      from: fromParam,
      to: toParam,
    });
    if (kudosMessage.trim()) {
      // Encode the message using base64 for more compact URL
      params.append('msg', encodeMessage(kudosMessage.trim()));
    }
    
    // Use company context if present in URL
    const basePath = company ? `/${company}/receive` : '/receive';
    const url = `${window.location.origin}${basePath}?${params.toString()}`;
    setShareableUrl(url);
    
    // Track ThanksBeer generation
    trackThanksBeerGenerated({
      hasSenderName: !!senderName.trim(),
      hasRecipientName: !!recipientName.trim(),
      hasMessage: !!kudosMessage.trim(),
      company,
    });
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareableUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      
      // Track link copy
      trackLinkCopied(shareableUrl);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleViewThanksBeer = () => {
    const url = new URL(shareableUrl);
    trackButtonClick('View ThanksBeer', 'GiveThanksPage');
    navigate(url.pathname + url.search);
  };

  const handleDrinkDemo = () => {
    trackButtonClick('Try Demo', 'GiveThanksPage');
    navigate('/receive-demo');
  };

  const handleCreateAnother = () => {
    setShareableUrl('');
    setSenderName('');
    setRecipientName('');
    setKudosMessage('');
    setCopied(false);
    trackButtonClick('Create Another', 'GiveThanksPage');
  };

  return (
    <div className={`give-thanks-page ${isChristmas ? 'christmas' : ''}`} style={{ backgroundImage: `url(${giveThanksImage})` }}>
      {isChristmas && <SnowflakesEffect />}
      {isChristmas && <div className="christmas-overlay" />}
      <Navigation />
      <div className="overlay">
        <div className="content-wrapper">
          <div className="left-content">
            <HeroCard>
              <div className="card-icon">
                <img src="/thanksbeer.ico" alt="Beer" className="card-icon-img" />
                🎁
              </div>
              
              {!shareableUrl ? (
                <>
                  <h2 className="headline">SEND A DIGITAL CHEERS. INSTANTLY.</h2>
                  
                  <div className="name-input-container">
                    <label htmlFor="sender-name" className="name-label">Your Name (Optional)</label>
                    <input
                      id="sender-name"
                      type="text"
                      className="name-input"
                      placeholder="Enter your name"
                      value={senderName}
                      onChange={(e) => setSenderName(e.target.value)}
                    />
                  </div>

                  <div className="name-input-container">
                    <label htmlFor="recipient-name" className="name-label">Who do you want to thank (Optional)</label>
                    <input
                      id="recipient-name"
                      type="text"
                      className="name-input"
                      placeholder="Enter recipient's name"
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                    />
                  </div>

                  <div className="name-input-container">
                    <label htmlFor="kudos-message" className="name-label">Kudos Message (Optional)</label>
                    <textarea
                      id="kudos-message"
                      className="kudos-textarea"
                      placeholder="Write a short kudos message..."
                      value={kudosMessage}
                      onChange={(e) => setKudosMessage(e.target.value)}
                      maxLength={200}
                      rows={3}
                    />
                    <span className="character-count">{kudosMessage.length}/200</span>
                  </div>
                  
                  <PrimaryButton onClick={handleGiveThanks}>
                    GIVE A THANKSBEER
                  </PrimaryButton>
                  
                  <div className="demo-section">
                    <p className="demo-description">Try it first!</p>
                    <button className="demo-button-small" onClick={handleDrinkDemo}>
                      🍺 Try a Demo ThanksBeer
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="headline">YOUR THANKSBEER IS READY!</h2>
                  <p className="share-instruction">Share this link to send your ThanksBeer:</p>
                  
                  <div className="url-display-container">
                    <div className="url-display">
                      {shareableUrl}
                    </div>
                  </div>
                  
                  <p className="link-blurb">
                    🍺 <em>One click. One moment. Instant gratitude delivered.</em>
                  </p>
                  
                  <div className="action-buttons">
                    <PrimaryButton onClick={handleCopyToClipboard}>
                      {copied ? '✓ COPIED!' : 'COPY TO CLIPBOARD'}
                    </PrimaryButton>
                    <PrimaryButton onClick={handleViewThanksBeer}>
                      VIEW THANKSBEER
                    </PrimaryButton>
                  </div>
                  
                  <button className="create-another-link" onClick={handleCreateAnother}>
                    Create Another ThanksBeer
                  </button>
                </>
              )}
            </HeroCard>
          </div>
          
          <div className="right-content">
            {/* Beer glass visible from hero image */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiveThanksPage;
